const DAY_MS = 86_400_000;

export type TrialAccessRole = "coach" | "leader";

export type TrialAccessState =
  | "preview"
  | "paid"
  | "comp"
  | "billing_transfer_pending"
  | "coach_led"
  | "unknown"
  | "not_started"
  | "active"
  | "ending_soon"
  | "ends_today"
  | "expired";

export type TrialAccessCtaVariant =
  | "explore_coach_veera"
  | "keep_full_access"
  | "choose_a_plan"
  | "view_plans";

export interface TrialAccessCta {
  variant: TrialAccessCtaVariant;
  label: string;
}

export interface ResolveTrialAccessPresentationInput {
  role: TrialAccessRole;
  isTrialEligible: boolean;
  trialStartedAt?: string | null;
  trialEndsAt?: string | null;
  timeZone: string;
  now?: Date;
  isPreview?: boolean;
  hasPaidAccess?: boolean;
  hasCompAccess?: boolean;
  hasExpiredAccess?: boolean;
  isBillingTransferPending?: boolean;
  isCoachLed?: boolean;
  hasMeaningfulActivation?: boolean;
}

export interface TrialAccessPresentation {
  state: TrialAccessState;
  trialStartedAt: string | null;
  trialEndsAt: string | null;
  daysRemaining: number | null;
  message: string | null;
  cta: TrialAccessCta | null;
}

const EXPLORE_CTA: TrialAccessCta = {
  variant: "explore_coach_veera",
  label: "Explore Coach Veera",
};

const KEEP_ACCESS_CTA: TrialAccessCta = {
  variant: "keep_full_access",
  label: "Keep full access",
};

const CHOOSE_PLAN_CTA: TrialAccessCta = {
  variant: "choose_a_plan",
  label: "Choose a plan",
};

const VIEW_PLANS_CTA: TrialAccessCta = {
  variant: "view_plans",
  label: "View plans",
};

export function resolveTrialAccessPresentation(
  input: ResolveTrialAccessPresentationInput,
): TrialAccessPresentation {
  const trialStartedAt = input.trialStartedAt ?? null;
  const trialEndsAt = input.trialEndsAt ?? null;

  const precedenceState = resolvePrecedenceState(input);
  if (precedenceState) {
    return suppressedState(precedenceState, trialStartedAt, trialEndsAt);
  }

  if (input.hasExpiredAccess) {
    return {
      state: "expired",
      trialStartedAt,
      trialEndsAt,
      daysRemaining: 0,
      message: null,
      cta: VIEW_PLANS_CTA,
    };
  }

  if (!input.isTrialEligible) {
    return suppressedState("unknown", trialStartedAt, trialEndsAt);
  }

  if (!trialStartedAt && !trialEndsAt) {
    return {
      state: "not_started",
      trialStartedAt,
      trialEndsAt,
      daysRemaining: null,
      message:
        input.role === "coach"
          ? "Your 7-day trial starts when your first leader joins"
          : "Your 7-day full-access trial has not started",
      cta: EXPLORE_CTA,
    };
  }

  const now = input.now ?? new Date();
  const startedAtMs = parseTimestamp(trialStartedAt);
  const endsAtMs = parseTimestamp(trialEndsAt);
  if (
    startedAtMs === null ||
    endsAtMs === null ||
    endsAtMs <= startedAtMs ||
    startedAtMs > now.getTime()
  ) {
    return suppressedState("unknown", trialStartedAt, trialEndsAt);
  }

  if (endsAtMs <= now.getTime()) {
    return {
      state: "expired",
      trialStartedAt,
      trialEndsAt,
      daysRemaining: 0,
      message: null,
      cta: VIEW_PLANS_CTA,
    };
  }

  const daysRemaining = calendarDaysBetween(now, new Date(endsAtMs), input.timeZone);
  if (daysRemaining === null || daysRemaining < 0) {
    return suppressedState("unknown", trialStartedAt, trialEndsAt);
  }

  if (daysRemaining === 0) {
    return {
      state: "ends_today",
      trialStartedAt,
      trialEndsAt,
      daysRemaining,
      message: "Trial ends today",
      cta: CHOOSE_PLAN_CTA,
    };
  }

  if (daysRemaining <= 3) {
    return {
      state: "ending_soon",
      trialStartedAt,
      trialEndsAt,
      daysRemaining,
      message: `${daysRemaining} ${daysRemaining === 1 ? "day" : "days"} left`,
      cta: KEEP_ACCESS_CTA,
    };
  }

  return {
    state: "active",
    trialStartedAt,
    trialEndsAt,
    daysRemaining,
    message: input.hasMeaningfulActivation
      ? `${daysRemaining} days left`
      : `Full access · ${daysRemaining} days left`,
    cta: input.hasMeaningfulActivation ? KEEP_ACCESS_CTA : EXPLORE_CTA,
  };
}

function resolvePrecedenceState(
  input: ResolveTrialAccessPresentationInput,
): Exclude<
  TrialAccessState,
  "unknown" | "not_started" | "active" | "ending_soon" | "ends_today" | "expired"
> | null {
  if (input.isPreview) return "preview";
  if (input.hasPaidAccess) return "paid";
  if (input.hasCompAccess) return "comp";
  if (input.isBillingTransferPending) return "billing_transfer_pending";
  if (input.isCoachLed) return "coach_led";
  return null;
}

function suppressedState(
  state: TrialAccessState,
  trialStartedAt: string | null,
  trialEndsAt: string | null,
): TrialAccessPresentation {
  return {
    state,
    trialStartedAt,
    trialEndsAt,
    daysRemaining: null,
    message: null,
    cta: null,
  };
}

function parseTimestamp(value: string | null): number | null {
  if (!value) return null;
  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) ? timestamp : null;
}

function calendarDaysBetween(start: Date, end: Date, timeZone: string): number | null {
  const startOrdinal = localDateOrdinal(start, timeZone);
  const endOrdinal = localDateOrdinal(end, timeZone);
  if (startOrdinal === null || endOrdinal === null) return null;
  return Math.round((endOrdinal - startOrdinal) / DAY_MS);
}

function localDateOrdinal(value: Date, timeZone: string): number | null {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(value);
    const year = Number(parts.find((part) => part.type === "year")?.value);
    const month = Number(parts.find((part) => part.type === "month")?.value);
    const day = Number(parts.find((part) => part.type === "day")?.value);
    if (![year, month, day].every(Number.isFinite)) return null;
    return Date.UTC(year, month - 1, day);
  } catch {
    return null;
  }
}
